import { useState, useEffect, useRef } from 'react';
import { X, Package, Send, CheckCircle, Truck, Upload, FileText, Download, Trash2, FolderOpen, Clock, MessageSquare, Printer, Loader2 } from 'lucide-react';
import { Card } from '../ui/card';
import { purchasingAPI, ASNDocument, ASNDocumentType, ASNMessage } from '../../lib/api';
import Changelog, { ChangelogEntry } from '../common/Changelog';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { useAuth } from '../../contexts/AuthContext';
import { ASNDetailModalProps, ASNScheduleDetail, DOCUMENT_TYPE_LABELS } from './asnModalTypes';
import { toast } from 'sonner';

export function ASNDetailModal({ isOpen, onClose, asn, onSubmit }: ASNDetailModalProps) {
  const confirm = useConfirm();
  // CR-4888 round 2: gate ASN workflow buttons by role.
  // Supplier owns 'submit' (Created → Submitted). Customer owns 'acknowledge'
  // (Submitted → In Transit). isSupplierUser hides customer-only actions from
  // suppliers; isCustomer suppresses 'Mark as Delivered' for call-off-sourced
  // ASNs (those are marked delivered via the GRN booking flow instead).
  const { user } = useAuth();
  const isSupplierUser = user?.platform === 'SUPPLIER';
  const isCustomer = user?.company_type === 'customer' || user?.platform === 'CUSTOMER';
  const [asnData, setAsnData] = useState(asn);
  const [loading, setLoading] = useState(false);

  // CR-3417: Document upload state
  const [documents, setDocuments] = useState<ASNDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<ASNDocumentType>('delivery_note');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CR-3859: Activity log state
  const [activityLog, setActivityLog] = useState<ChangelogEntry[]>([]);
  const [loadingActivityLog, setLoadingActivityLog] = useState(false);

  // CR-3860: Communication state
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [messages, setMessages] = useState<ASNMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    if (isOpen && asn.id) {
      fetchASNDetails();
      fetchDocuments();
      fetchActivityLog();
    }
  }, [isOpen, asn.id]);

  const fetchASNDetails = async () => {
    try {
      const response = await purchasingAPI.getASN(asn.id);
      setAsnData(response.data);
    } catch (error) {
      console.error('Failed to fetch ASN details:', error);
    }
  };

  const fetchActivityLog = async () => {
    if (!asn.id) return;
    try {
      setLoadingActivityLog(true);
      const response = await purchasingAPI.getChangelogs({ asn: asn.id, ordering: '-change_date', page_size: 50 });
      const transformedLogs: ChangelogEntry[] = (response.data.results || []).map((log) => ({
        id: log.id,
        change_date: log.change_date,
        changed_by: log.changed_by_details?.full_name || 'System',
        field_changed: log.field_changed,
        old_value: log.old_value,
        new_value: log.new_value,
        reason: log.reason || '',
        change_type: log.field_changed || '',
      }));
      setActivityLog(transformedLogs);
    } catch (error) {
      console.error('Failed to fetch ASN activity log:', error);
    } finally {
      setLoadingActivityLog(false);
    }
  };

  const fetchMessages = async () => {
    if (!asn.id) return;
    try {
      setLoadingMessages(true);
      const res = await purchasingAPI.getASNMessages({ asn: asn.id, page_size: 100 });
      setMessages(res.data.results || []);
    } catch (error) {
      console.error('Failed to fetch ASN messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !asn.id) return;
    try {
      setSubmittingNote(true);
      await purchasingAPI.createASNMessage({ asn: asn.id, message_text: newNote.trim() });
      setNewNote('');
      await fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSubmittingNote(false);
    }
  };

  const fetchDocuments = async () => {
    if (!asn.id) return;
    try {
      setLoadingDocuments(true);
      const response = await purchasingAPI.getASNDocumentsByASN(asn.id);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch ASN documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // File size validation
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size exceeds 50MB limit');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // File type validation
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'rar', '7z', 'jpg', 'jpeg', 'png', 'gif'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      toast.error('Unsupported file type. Supported: PDF, Word, Excel, Images, ZIP');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('asn', asn.id.toString());
    formData.append('document_type', selectedDocType);
    formData.append('file', file);
    formData.append('file_name', file.name);

    try {
      setUploadingDocument(true);
      await purchasingAPI.uploadASNDocument(formData);
      await fetchDocuments();
      toast.success('Document uploaded successfully');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error: unknown) {
      console.error('Failed to upload document:', error);
      toast.error(getApiErrorMessage(error, 'Failed to upload document'));
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDownloadDocument = async (doc: ASNDocument) => {
    try {
      const response = await purchasingAPI.downloadASNDocument(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    const ok = await confirm({ title: 'Delete Document', description: 'Are you sure you want to delete this document?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      await purchasingAPI.deleteASNDocument(docId);
      await fetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const [printing, setPrinting] = useState(false);

  const handlePrintASN = async () => {
    setPrinting(true);
    try {
      const response = await purchasingAPI.printASN(asnData.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error: unknown) {
      console.error('Failed to print ASN:', error);
    } finally {
      setPrinting(false);
    }
  };

  const handleWorkflowAction = async (action: string, data?: { actual_delivery_date?: string }) => {
    setLoading(true);
    try {
      if (action === 'submit') await purchasingAPI.submitASN(asnData.id);
      else if (action === 'acknowledge') await purchasingAPI.acknowledgeASN(asnData.id);
      else if (action === 'mark_delivered') await purchasingAPI.markASNDelivered(asnData.id, data || {});
      await fetchASNDetails();
      await onSubmit({});
    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      alert(getApiErrorMessage(error, 'Failed to process workflow action'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      created: { label: 'Created', class: 'bg-gray-100 text-gray-800' },
      submitted: { label: 'Submitted', class: 'bg-blue-100 text-blue-800' },
      in_transit: { label: 'In Transit', class: 'bg-yellow-100 text-yellow-800' },
      delivered: { label: 'Delivered', class: 'bg-green-100 text-green-800' },
      received: { label: 'Received', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
    };
    const config = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
  };

  const { formatDate: contextFormatDate } = useFormatters();
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  const [detailTab, setDetailTab] = useState<'lines' | 'documents' | 'communication' | 'activity'>('lines');

  // Fetch messages when communication tab is opened
  useEffect(() => {
    if (detailTab === 'communication' && asn.id) {
      fetchMessages();
    }
  }, [detailTab, asn.id]);

  if (!isOpen) return null;

  const scheduleCount = asnData.schedules_details?.length || 0;
  const deliveryLineCount = asnData.delivery_lines_details?.length || 0;
  const lineCount = scheduleCount + deliveryLineCount;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <Card className="w-full max-w-5xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">ASN Details</h3>
              {getStatusBadge(asnData.status)}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Compact Summary Grid */}
          <div className="px-6 py-4 border-b bg-muted/20">
            <div className="grid grid-cols-4 gap-x-8 gap-y-3 text-sm">
              <div><span className="text-muted-foreground">ASN Number</span><p className="font-medium">{asnData.asn_number}</p></div>
              <div><span className="text-muted-foreground">Supplier</span><p className="font-medium">{asnData.supplier_details?.name || '-'}</p></div>
              <div><span className="text-muted-foreground">Customer</span><p className="font-medium">{asnData.customer_details?.name || '-'}</p></div>
              <div><span className="text-muted-foreground">Courier</span><p className="font-medium">{asnData.courier?.toUpperCase() || '-'}</p></div>
              <div><span className="text-muted-foreground">Tracking Number</span><p className="font-medium font-mono">{asnData.tracking_number || '-'}</p></div>
              <div><span className="text-muted-foreground">Invoice Number</span><p className="font-medium font-mono">{asnData.invoice_number || '-'}</p></div>
              <div><span className="text-muted-foreground">Transport</span><p className="font-medium capitalize">{asnData.method_of_transport || '-'}</p></div>
              <div><span className="text-muted-foreground">Expected Delivery</span><p className="font-medium">{formatDate(asnData.expected_delivery_date)}</p></div>
              <div><span className="text-muted-foreground">Actual Delivery</span><p className="font-medium">{formatDate(asnData.actual_delivery_date)}</p></div>
              <div><span className="text-muted-foreground">Weight (kg)</span><p className="font-medium">{asnData.weight_kg || '-'}</p></div>
              <div><span className="text-muted-foreground">Distance (km)</span><p className="font-medium">{asnData.distance_km || '-'}</p></div>
              <div><span className="text-muted-foreground">Carbon (kg CO2)</span><p className="font-medium text-green-700">{asnData.carbon_emission_kg || asnData.calculated_carbon_emission || '-'}</p></div>
              {asnData.notes && <div><span className="text-muted-foreground">Notes</span><p className="font-medium truncate" title={asnData.notes}>{asnData.notes}</p></div>}
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b px-6">
            <div className="flex gap-1">
              {([
                { key: 'lines' as const, label: `Lines (${lineCount})`, icon: Package },
                { key: 'documents' as const, label: `Documents (${documents.length})`, icon: FolderOpen },
                { key: 'communication' as const, label: 'Communication', icon: MessageSquare },
                { key: 'activity' as const, label: 'Activity Log', icon: Clock },
              ]).map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setDetailTab(key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${detailTab === key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`}>
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Lines Tab */}
            {detailTab === 'lines' && (
              <>
                {asnData.delivery_lines_details && asnData.delivery_lines_details.length > 0 ? (
                  <div className="overflow-x-auto">
                    {/* CR-4888 round 2 / Error 3: hide the Call-Off column when
                        the ASN came from the Excel-import flow (source_type
                        !== 'CallOff'). The synthetic SUP-IMP- references the
                        import path generates aren't real call-offs and the
                        column rendered as noise next to the Sales Order /
                        PO column the user actually cares about. */}
                    {(() => {
                      const showCallOffColumn = asnData.source_type === 'CallOff';
                      return (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-2 px-3 font-medium">Delivery #</th>
                          {showCallOffColumn && <th className="text-left py-2 px-3 font-medium">Call-Off</th>}
                          <th className="text-left py-2 px-3 font-medium">Part Number</th>
                          <th className="text-left py-2 px-3 font-medium">Description</th>
                          <th className="text-right py-2 px-3 font-medium">Qty Released</th>
                          <th className="text-left py-2 px-3 font-medium">Delivery Option</th>
                          <th className="text-left py-2 px-3 font-medium">Sales Order</th>
                          <th className="text-left py-2 px-3 font-medium">PO / Line</th>
                        </tr>
                      </thead>
                      <tbody>
                        {asnData.delivery_lines_details.map((line) => (
                          <tr key={line.id} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3 font-mono text-xs">{line.delivery_number || '-'}</td>
                            {showCallOffColumn && <td className="py-2 px-3 font-mono text-xs">{line.call_off_reference || '-'}</td>}
                            <td className="py-2 px-3 font-mono text-xs">{line.part_number || '-'}</td>
                            <td className="py-2 px-3 text-xs max-w-[200px] truncate" title={line.part_description}>{line.part_description || '-'}</td>
                            <td className="py-2 px-3 text-right">{line.qty_released || '-'}</td>
                            <td className="py-2 px-3 text-xs">{line.delivery_option || '-'}</td>
                            <td className="py-2 px-3 text-xs">{line.sales_order || '-'}</td>
                            <td className="py-2 px-3 text-xs">
                              {line.po_order_number ? (
                                <>
                                  <div className="font-medium">{line.po_order_number}</div>
                                  {line.po_line_number && <div className="text-xs text-muted-foreground">Line {line.po_line_number}</div>}
                                </>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                      );
                    })()}
                  </div>
                ) : asnData.schedules_details && asnData.schedules_details.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-2 px-3 font-medium">PO / Line</th>
                          <th className="text-left py-2 px-3 font-medium">Part Number</th>
                          <th className="text-left py-2 px-3 font-medium">Description</th>
                          <th className="text-right py-2 px-3 font-medium">Schedule Qty</th>
                          <th className="text-right py-2 px-3 font-medium">Shipped Qty</th>
                          <th className="text-right py-2 px-3 font-medium">Balance</th>
                          <th className="text-center py-2 px-3 font-medium">Status</th>
                          <th className="text-left py-2 px-3 font-medium">Due Date</th>
                          <th className="text-left py-2 px-3 font-medium">GRN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {asnData.schedules_details.map((schedule: ASNScheduleDetail, idx: number) => {
                          const getScheduleStatusBadge = (status: string) => {
                            const statusMap: Record<string, { label: string; class: string }> = {
                              scheduled: { label: 'Scheduled', class: 'bg-blue-100 text-blue-800' },
                              partially_shipped: { label: 'Partial', class: 'bg-yellow-100 text-yellow-800' },
                              shipped: { label: 'Shipped', class: 'bg-green-100 text-green-800' },
                              cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
                            };
                            const config = statusMap[status] || { label: status || '-', class: 'bg-gray-100 text-gray-800' };
                            return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
                          };

                          return (
                            <tr key={idx} className="border-b hover:bg-muted/30">
                              <td className="py-2 px-3">
                                <div className="font-medium">{schedule.po_number || schedule.po_line_details?.po_details?.po_number || '-'}</div>
                                <div className="text-xs text-muted-foreground">Line {schedule.line_number || schedule.po_line_details?.line_number}</div>
                              </td>
                              <td className="py-2 px-3 font-mono text-xs">{schedule.part_number || schedule.po_line_details?.part_number || '-'}</td>
                              <td className="py-2 px-3 text-xs max-w-[200px] truncate" title={schedule.description || schedule.po_line_details?.description}>{schedule.description || schedule.po_line_details?.description || '-'}</td>
                              <td className="py-2 px-3 text-right">{schedule.schedule_qty || schedule.quantity || '-'}<span className="text-xs text-muted-foreground ml-1">{schedule.unit_of_measure || schedule.po_line_details?.unit_of_measure || ''}</span></td>
                              <td className="py-2 px-3 text-right font-medium text-green-700">{schedule.shipped_qty ?? '-'}</td>
                              <td className="py-2 px-3 text-right"><span className={parseFloat(schedule.balance_qty || '0') > 0 ? 'text-orange-600 font-medium' : ''}>{schedule.balance_qty ?? '-'}</span></td>
                              <td className="py-2 px-3 text-center">{getScheduleStatusBadge(schedule.status || '')}</td>
                              <td className="py-2 px-3">{formatDate(schedule.schedule_date || schedule.due_date)}</td>
                              <td className="py-2 px-3">
                                {schedule.grn_number ? (
                                  <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">{schedule.grn_number}</span>
                                ) : schedule.status === 'shipped' ? (
                                  <span className="text-xs text-muted-foreground italic">Awaiting GRN</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No line details available</div>
                )}
              </>
            )}

            {/* Documents Tab */}
            {detailTab === 'documents' && (
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Document Type</label>
                      <select value={selectedDocType} onChange={(e) => setSelectedDocType(e.target.value as ASNDocumentType)} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                        {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end gap-2">
                      <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif" />
                      <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingDocument} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Supported: PDF, Word, Excel, Images, ZIP (max 50MB)</p>
                </div>

                {loadingDocuments ? (
                  <div className="text-sm text-muted-foreground py-4 text-center">Loading documents...</div>
                ) : documents.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No documents uploaded yet</div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-2 px-3 font-medium">Document</th>
                          <th className="text-left py-2 px-3 font-medium">Type</th>
                          <th className="text-left py-2 px-3 font-medium">Size</th>
                          <th className="text-left py-2 px-3 font-medium">Uploaded</th>
                          <th className="text-right py-2 px-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc) => (
                          <tr key={doc.id} className="border-b last:border-b-0 hover:bg-muted/30">
                            <td className="py-2 px-3"><div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span className="font-medium truncate max-w-[200px]" title={doc.file_name}>{doc.file_name}</span></div></td>
                            <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}</span></td>
                            <td className="py-2 px-3 text-muted-foreground">{doc.formatted_file_size || '-'}</td>
                            <td className="py-2 px-3 text-muted-foreground">
                              <div>{new Date(doc.upload_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                              {doc.uploaded_by_details && <div className="text-xs">by {doc.uploaded_by_details.first_name} {doc.uploaded_by_details.last_name}</div>}
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleDownloadDocument(doc)} className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors text-blue-600" title="Download"><Download className="h-4 w-4" /></button>
                                <button onClick={() => handleDeleteDocument(doc.id)} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Communication Tab */}
            {detailTab === 'communication' && (
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Add a note or message</label>
                  <div className="flex gap-2">
                    <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Enter your message..." rows={3} className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                    <button onClick={handleAddNote} disabled={submittingNote || !newNote.trim()} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 self-end">
                      {submittingNote ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
                {loadingMessages ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">Loading messages...</div>
                ) : messages.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-auto">
                    {messages.map((msg) => (
                      <div key={msg.id} className="border rounded-lg p-3 bg-white">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium">{msg.sender_details?.full_name || 'Unknown'}</span>
                          <span className="text-xs text-muted-foreground">{new Date(msg.sent_date).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{msg.message_text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No messages yet</div>
                )}
              </div>
            )}

            {/* Activity Log Tab */}
            {detailTab === 'activity' && (
              <>
                {loadingActivityLog ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">Loading activity log...</div>
                ) : activityLog.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No activity recorded yet</div>
                ) : (
                  <Changelog entries={activityLog} maxHeight="400px" emptyMessage="No activity recorded" />
                )}
              </>
            )}
          </div>

          {/* Footer with Workflow Actions */}
          <div className="flex justify-between items-center p-4 border-t">
            <div className="flex gap-3">
              {/* CR-4888 round 2 / Error 9: Submit to Customer is supplier-only.
                  Was rendering for customers too whenever status='created'. */}
              {asnData.status === 'created' && isSupplierUser && (
                <button onClick={() => handleWorkflowAction('submit')} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  <Send className="h-4 w-4" />Submit to Customer
                </button>
              )}
              {/* CR-4888 round 2 / Error 10: Acknowledge is customer-only.
                  Supplier was being shown the button but the API rejects it
                  with "only the customer can acknowledge this ASN" — so the
                  button violated its own permission contract. */}
              {asnData.status === 'submitted' && !isSupplierUser && (
                <button onClick={() => handleWorkflowAction('acknowledge')} disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />Acknowledge (In Transit)
                </button>
              )}
              {/* Mark as Delivered: kept for non-call-off ASNs (customer can
                  manually mark a PO-driven shipment delivered). Suppressed for
                  call-off-sourced ASNs — those are marked delivered when the
                  customer books a GRN against the shipment in their ERP. */}
              {(asnData.status === 'in_transit' || asnData.status === 'submitted') && !(isCustomer && asnData.source_type === 'CallOff') && (
                <button onClick={() => { const actualDate = new Date().toISOString().split('T')[0]; handleWorkflowAction('mark_delivered', { actual_delivery_date: actualDate }); }} disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />Mark as Delivered
                </button>
              )}
              <button onClick={handlePrintASN} disabled={printing} className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                Print ASN
              </button>
            </div>
            <button onClick={onClose} disabled={loading} className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50">Close</button>
          </div>
        </Card>
      </div>
    </>
  );
}
